import { Box, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { DisplayTable } from '@/components/tables/DisplayTable/DisplayTable';
import { TableRowSkeleton } from '@/components/tables/TableRowSkeleton/TableRowSkeleton';
import { getKindFromProvider } from '@/modules/assetProviderIntegrations/assetProviderIntegrations.utils';
import { useEntityTargets } from '@/modules/cashflows/CashflowDrawer/hooks/useEntityTargets';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { COLORS } from '@/styles/tokens/colors';

import { useManageIntegrationDataContext } from '../../context/manageIntegrationData.context';
import { CashflowsImportFormShape } from './CashflowsImport.types';
import {
  IntegrationCashflowNode,
  toCashflowsImportForm,
} from './CashflowsImport.utils';
import { CashflowsImportEditDrawer } from './CashflowsImportEditDrawer';
import { CashflowsImportRow } from './CashflowsImportRow';
import { useCashflowsImportQuery } from './graphql/CashflowsImport.generated';
import { CashflowsImportForm } from './hooks/useCashflowsImportForm';

const COLUMNS = [
  { headerName: '', width: 40 },
  { headerName: 'Unlinked cash flows', flex: 1 },
  { headerName: 'Amount', width: 180 },
  { headerName: 'Entity', width: 280 },
  { headerName: '', width: 48 },
];

interface CashflowsImportPaneProps {
  form: CashflowsImportForm;
}

export function CashflowsImportPane({ form }: CashflowsImportPaneProps) {
  const { householdId } = useManageIntegrationDataContext();
  const { linkedHouseholdAssetProvider } = useHouseholdDetailsContext();
  const { formMethods, seed } = form;
  const integrationClientKind = linkedHouseholdAssetProvider
    ? getKindFromProvider(linkedHouseholdAssetProvider)
    : undefined;
  const { data, loading } = useCashflowsImportQuery({
    variables: {
      householdsLike: { id: householdId },
      integrationClientKind,
    },
    skip: !integrationClientKind,
    fetchPolicy: 'cache-and-network',
  });
  const cashflows = useMemo(
    () =>
      data?.integrationCashflows.edges
        ?.map((edge) => edge?.node)
        .filter((node): node is IntegrationCashflowNode => node != null) ?? [],
    [data]
  );

  useEffect(() => {
    if (!loading) seed(toCashflowsImportForm(cashflows));
  }, [cashflows, loading, seed]);

  return (
    <FormProvider {...formMethods}>
      <CashflowsImportPaneInner loading={loading} />
    </FormProvider>
  );
}

function CashflowsImportPaneInner({ loading }: { loading: boolean }) {
  const { householdId, isSaving } = useManageIntegrationDataContext();
  const { control } = useFormContext<CashflowsImportFormShape>();
  const [entityOptions] = useEntityTargets(householdId);
  const [editingCashflowId, setEditingCashflowId] = useState<string | null>(
    null
  );
  const cashflows = useWatch({ control, name: 'cashflows' });
  const cashflowIds = useMemo(() => Object.keys(cashflows ?? {}), [cashflows]);

  if (!loading && cashflowIds.length === 0) {
    return (
      <Box py={4} textAlign="center">
        <Typography variant="body-s" color={COLORS.TEXT.SECONDARY}>
          No unlinked cash flows found for this household.
        </Typography>
      </Box>
    );
  }

  return (
    <FormFieldsDisabledProvider isDisabled={isSaving}>
      <DisplayTable columns={COLUMNS}>
        {loading
          ? Array.from({ length: 4 }, (_, index) => (
              <TableRowSkeleton key={index} columns={COLUMNS.length} />
            ))
          : cashflowIds.map((cashflowId) => (
              <CashflowsImportRow
                key={cashflowId}
                cashflowId={cashflowId}
                entityOptions={entityOptions}
                onEdit={setEditingCashflowId}
              />
            ))}
      </DisplayTable>

      {editingCashflowId && (
        <CashflowsImportEditDrawer
          cashflowId={editingCashflowId}
          onClose={() => setEditingCashflowId(null)}
        />
      )}
    </FormFieldsDisabledProvider>
  );
}
